import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPendingRecording, importPendingRecording, getClients } from '../api/client';
import { useAuth } from '../context/AuthContext';
import LoadingLeaf from '../components/LoadingLeaf';

export default function ImportRecording() {
  const { source, externalId } = useParams();
  const [recording, setRecording] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [speakerMap, setSpeakerMap] = useState({});
  const [sessionType, setSessionType] = useState('individual');

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) loadData();
  }, [user, source, externalId]);

  const loadData = async () => {
    try {
      const [recRes, clientsRes] = await Promise.all([
        getPendingRecording(source, externalId),
        getClients(),
      ]);
      setRecording(recRes.data);
      setAllClients(clientsRes.data || []);

      // Pre-populate speaker map with detected participants
      if (recRes.data?.participant_names?.length) {
        const initial = {};
        recRes.data.participant_names.forEach(name => { initial[name] = 'unknown'; });
        setSpeakerMap(initial);
      }
    } catch (err) {
      setError('Could not load recording.');
    } finally {
      setLoading(false);
    }
  };

  const toggleClient = (id) => {
    setSelectedClientIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
    // Auto-set session type based on count
    const newCount = selectedClientIds.includes(id)
      ? selectedClientIds.length - 1
      : selectedClientIds.length + 1;
    if (newCount > 1) setSessionType('group');
    else setSessionType('individual');
  };

  const handleImport = async () => {
    if (selectedClientIds.length === 0) {
      setError('Please select at least one client.');
      return;
    }
    setError('');
    setImporting(true);
    try {
      const res = await importPendingRecording(source, externalId, {
        client_ids: selectedClientIds,
        speaker_map: speakerMap,
        session_type: sessionType,
      });
      setSuccess(`Session created for ${res.data.message.split('for')[1] || ' your clients'}. Redirecting…`);
      setTimeout(() => navigate('/sessions'), 1800);
    } catch (err) {
      setError('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <LoadingLeaf fullScreen size={72} />;

  if (!recording) return (
    <div style={styles.page}>
      <div style={styles.errorAlert}>Recording not found.</div>
    </div>
  );

  const participants = recording.participant_names || [];

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={() => navigate('/pending-recordings')}>← Pending recordings</button>

      <div style={styles.pageTitle}>Import Recording</div>
      <div style={styles.pageSubtitle}>{recording.title}</div>

      {error && <div style={styles.errorAlert}>{error}</div>}
      {success && <div style={styles.successAlert}>{success}</div>}

      {/* Recording details */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Recording details</div>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Source</span>
          <span style={styles.detailValue}>{recording.source === 'zoom' ? '🔵 Zoom' : '🔴 Google Meet'}</span>
        </div>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Recorded</span>
          <span style={styles.detailValue}>
            {recording.recorded_at ? new Date(recording.recorded_at).toLocaleString('default', { dateStyle: 'long', timeStyle: 'short' }) : '—'}
          </span>
        </div>
        {participants.length > 0 && (
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Participants</span>
            <div style={styles.tagRow}>
              {participants.map((name, i) => <span key={i} style={styles.tag}>{name}</span>)}
            </div>
          </div>
        )}
        {recording.transcript_text && (
          <div style={{ ...styles.detailRow, flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <span style={styles.detailLabel}>Transcript preview</span>
            <div style={styles.transcriptPreview}>
              {recording.transcript_text.slice(0, 400)}{recording.transcript_text.length > 400 ? '…' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Client selection */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Select clients</div>
        <div style={styles.sectionSub}>Which of your clients were in this session?</div>
        {allClients.length === 0 ? (
          <div style={styles.emptyClients}>No clients yet — add clients first.</div>
        ) : (
          <div style={styles.clientGrid}>
            {allClients.map(client => {
              const selected = selectedClientIds.includes(client.id);
              return (
                <button
                  key={client.id}
                  style={{ ...styles.clientBtn, ...(selected ? styles.clientBtnSelected : {}) }}
                  onClick={() => toggleClient(client.id)}
                >
                  <div style={styles.clientInitials}>
                    {client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div style={styles.clientName}>{client.name}</div>
                  {selected && <span style={styles.checkmark}>✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Session type */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Session type</div>
        <div style={styles.radioRow}>
          {['individual', 'group'].map(type => (
            <label key={type} style={styles.radioLabel}>
              <input
                type="radio"
                name="sessionType"
                value={type}
                checked={sessionType === type}
                onChange={() => setSessionType(type)}
                style={{ marginRight: '8px' }}
              />
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </label>
          ))}
        </div>
      </div>

      {/* Speaker mapping */}
      {participants.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Map speakers</div>
          <div style={styles.sectionSub}>Tell the AI who each speaker is so summaries are accurate.</div>
          <div style={styles.speakerList}>
            {participants.map(name => (
              <div key={name} style={styles.speakerRow}>
                <div style={styles.speakerName}>{name}</div>
                <select
                  style={styles.speakerSelect}
                  value={speakerMap[name] || 'unknown'}
                  onChange={(e) => setSpeakerMap(prev => ({ ...prev, [name]: e.target.value }))}
                >
                  <option value="unknown">Unknown / Skip</option>
                  <option value="coach">Coach (me)</option>
                  {allClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={styles.actionRow}>
        <button
          style={{ ...styles.importBtn, ...(importing || selectedClientIds.length === 0 ? styles.importBtnDisabled : {}) }}
          onClick={handleImport}
          disabled={importing || selectedClientIds.length === 0}
        >
          {importing ? 'Importing…' : 'Import as Session'}
        </button>
        <button style={styles.cancelBtn} onClick={() => navigate('/pending-recordings')} disabled={importing}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif", maxWidth: '760px' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  backBtn: { background: 'none', border: 'none', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', padding: '0', marginBottom: '24px', fontWeight: '500' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '28px' },
  section: { background: 'white', border: '1px solid var(--border)', borderRadius: '14px', padding: '22px 26px', marginBottom: '20px', boxShadow: 'var(--shadow-sm)' },
  sectionTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '16px', color: 'var(--sage)', marginBottom: '6px', paddingBottom: '10px', borderBottom: '1px solid var(--border-light)' },
  sectionSub: { fontSize: '13px', color: 'var(--text-soft)', marginBottom: '16px', marginTop: '4px' },
  detailRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' },
  detailLabel: { fontSize: '12px', fontWeight: '600', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: '100px' },
  detailValue: { fontSize: '14px', color: 'var(--text-dark)' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  tag: { background: 'var(--mint)', color: 'var(--sage)', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  transcriptPreview: { background: 'var(--cream)', borderRadius: '8px', padding: '12px 14px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-mid)', lineHeight: 1.7, width: '100%', boxSizing: 'border-box' },
  clientGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' },
  clientBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'white', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", position: 'relative', textAlign: 'left' },
  clientBtnSelected: { background: 'var(--mint)', borderColor: 'var(--sage)' },
  clientInitials: { width: '30px', height: '30px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
  clientName: { fontSize: '13.5px', color: 'var(--text-dark)', fontWeight: '500', flex: 1 },
  checkmark: { color: 'var(--sage)', fontWeight: '700', fontSize: '14px' },
  emptyClients: { fontSize: '13.5px', color: 'var(--text-faint)', fontStyle: 'italic' },
  radioRow: { display: 'flex', gap: '24px' },
  radioLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--text-mid)', cursor: 'pointer' },
  speakerList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  speakerRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  speakerName: { fontSize: '14px', color: 'var(--text-dark)', minWidth: '140px', fontWeight: '500' },
  speakerSelect: { flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', color: 'var(--text-dark)', background: 'white', cursor: 'pointer' },
  actionRow: { display: 'flex', gap: '12px', marginTop: '8px' },
  importBtn: { padding: '12px 28px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  importBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  cancelBtn: { padding: '12px 20px', borderRadius: '10px', background: 'white', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', cursor: 'pointer' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '20px' },
  successAlert: { background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '20px', fontWeight: '500' },
};
